"use client"

import { useState, useCallback } from "react"
import { ArrowRight, ArrowLeft, Sparkles, RotateCcw } from "lucide-react"

// ── Quiz questions — data-informed, Cosmic Match energy ──

type Answer = {
  label: string
  value: number // 0–3 where 0 = strong "don't go", 3 = strong "go"
  insight: string // cosmic insight shown in results
}

type Question = {
  id: string
  category: string
  cosmicPrompt: string // mystical framing
  question: string
  subtext: string // data-informed context
  answers: Answer[]
}

const QUESTIONS: Question[] = [
  {
    id: "financial_runway",
    category: "Money",
    cosmicPrompt: "The universe is checking your accounts.",
    question: "Can you cover your living costs — rent at home, bills, food — while you're away, without going into debt?",
    subtext: "Even \"free\" residencies cost $2,000–$5,000+ when you factor in travel, lost income, and rent back home. One artist documented spending $9,000 total for a residency in France. UK artists earn an average of just £12,500/year. 30% of artists surveyed in LA went without basic needs due to lack of funds. The cosmos respect a budget.",
    answers: [
      { label: "I'd need to take on debt or drain my emergency fund", value: 0, insight: "The stars see credit card interest in your future. That's not cosmic — that's compounding." },
      { label: "It would be tight, but I could make it work without borrowing", value: 2, insight: "Tight but possible. The universe rewards calculated risks." },
      { label: "I have savings set aside or the program covers most costs", value: 3, insight: "Financially aligned. The cosmos approve of preparation." },
      { label: "I honestly haven't done the math yet", value: 1, insight: "The universe doesn't do vibes-only budgeting. Do the math, then ask again." },
    ],
  },
  {
    id: "emergency_buffer",
    category: "Money",
    cosmicPrompt: "Mercury has a question about your safety net.",
    question: "If something unexpected happened during the residency — a medical bill, a broken laptop, a family emergency — could you handle it?",
    subtext: "Artists regularly report unforeseen costs of $500–$2,000 during residencies: emergency travel home, equipment repairs, materials you didn't anticipate. The Fractured Atlas data shows most artists underestimate residency costs by 30–40%.",
    answers: [
      { label: "I'd be in serious trouble — no buffer at all", value: 0, insight: "The stars don't do GoFundMe. An emergency fund isn't optional." },
      { label: "I could scrape together $500–$1,000 if I had to", value: 1, insight: "A thin buffer. The cosmos suggest padding it before you pack." },
      { label: "I have an emergency fund that could absorb a surprise", value: 3, insight: "Prepared for chaos. The universe respects that." },
      { label: "The program provides a stipend that would help cover surprises", value: 2, insight: "A stipend helps. Programs like Bemis offer $1,000/month — factor that into your safety math." },
    ],
  },
  {
    id: "lost_income",
    category: "Money",
    cosmicPrompt: "Your bank account's aura is flickering.",
    question: "What happens to your income while you're at the residency?",
    subtext: "Lost income is the single biggest hidden cost of residencies. W.A.G.E. puts it bluntly: \"Most artists can't afford to forfeit the little pay they might already have by leaving their jobs.\" One artist was so stressed about lost income during her residency that she couldn't enjoy it. If you have paid vacation, you'll burn it here — no recovery time left.",
    answers: [
      { label: "I'll lose my primary income entirely with no replacement", value: 0, insight: "The cosmos say: an opportunity that destabilizes your life isn't an opportunity. It's a crisis with better branding." },
      { label: "I'll lose some income but have savings or a stipend to offset it", value: 2, insight: "Partial coverage. The math works if you've already done it." },
      { label: "I can work remotely or my income continues while I'm away", value: 3, insight: "Income-neutral. The stars are practically pushing you out the door." },
      { label: "I'm between gigs anyway — the timing actually works", value: 3, insight: "Cosmic timing. The gap in your schedule is not accidental." },
    ],
  },
  {
    id: "what_it_provides",
    category: "Value",
    cosmicPrompt: "The celestial bodies are reading the fine print.",
    question: "What is this opportunity actually providing that you can't get at home?",
    subtext: "The most transformative residencies offer something specific: dedicated studio space, a new geography, a curated peer cohort, access to equipment, or uninterrupted time. \"Prestige\" alone doesn't pay rent.",
    answers: [
      { label: "Mostly the name on my CV — the facilities aren't special", value: 1, insight: "A line on your CV is worth something. But the cosmos want to know: is it worth what you're paying?" },
      { label: "Time and space I genuinely can't create at home", value: 3, insight: "The universe built residencies for exactly this reason. Go." },
      { label: "Access to specific equipment, landscape, or community I need", value: 3, insight: "You've identified something concrete. That's not desire — that's direction." },
      { label: "I'm not entirely sure — it just seemed like a good opportunity", value: 0, insight: "\"Seemed like a good opportunity\" is how the cosmos describe most regrets. Get specific or get honest." },
    ],
  },
  {
    id: "career_timing",
    category: "Timing",
    cosmicPrompt: "The stars are checking your calendar.",
    question: "Where are you in your practice right now?",
    subtext: "Research from the Alliance of Artist Communities shows that residencies have the most impact when artists are at a transition point — starting a new body of work, shifting mediums, or needing to break out of a rut.",
    answers: [
      { label: "I'm stuck or stagnant and need a reset", value: 3, insight: "The cosmos prescribe a change of context. Your practice is asking for it." },
      { label: "I'm in a productive flow and don't want to interrupt it", value: 1, insight: "Momentum is sacred. The stars say: don't fix what isn't broken." },
      { label: "I'm starting something new and need space to explore", value: 3, insight: "A new chapter needs a new room. The universe is offering one." },
      { label: "I'm not sure — I just applied because the deadline was there", value: 0, insight: "Applying and accepting are different commitments. The cosmos want you to know the difference." },
    ],
  },
  {
    id: "network_value",
    category: "Growth",
    cosmicPrompt: "Your artistic orbit is about to shift.",
    question: "Will this residency meaningfully expand your network or community?",
    subtext: "Alumni surveys consistently show that peer connections are the #1 lasting benefit of residencies — above the work produced, the CV line, or the stipend. Christina Pataialii's 2019 Gasworks residency led directly to the New Museum Triennial. Ibrahim Mahama went from a Gasworks residency in 2013 to the Venice Biennale by 2015. The permanent expansion of your professional circle is the real ROI.",
    answers: [
      { label: "Yes — the cohort, curators, or mentors are a genuine draw", value: 3, insight: "The people you'll meet there are already shaping your future. The cosmos confirmed it." },
      { label: "Maybe — I don't know much about who else will be there", value: 1, insight: "Research the alumni. The universe rewards due diligence." },
      { label: "Not really — I'm mostly going for the solitude and space", value: 2, insight: "Solitude has its own value. But don't underestimate the stranger in the next studio." },
      { label: "I already know most of the people involved", value: 1, insight: "Comfort is nice. Growth lives elsewhere. The cosmos are just saying." },
    ],
  },
  {
    id: "geography",
    category: "Place",
    cosmicPrompt: "The earth's energy grid has a suggestion.",
    question: "Does the location itself matter for your work?",
    subtext: "Geography isn't just scenery. Photographers chase specific light. Writers need unfamiliar rooms. Ceramicists need different clay. Sound artists need different acoustics. The place should serve the practice, not just the Instagram.",
    answers: [
      { label: "Yes — the landscape, culture, or context directly feeds my work", value: 3, insight: "The coordinates matter. Your practice has been pointing there." },
      { label: "Somewhat — any change of scenery would help", value: 2, insight: "A change of context shakes loose what routine has cemented. The cosmos validate this." },
      { label: "Not really — I could do this work anywhere with a studio", value: 1, insight: "If location doesn't matter, make sure what does matter is worth the disruption." },
      { label: "I picked it partly because I've always wanted to visit", value: 1, insight: "Tourism and residency are different frequencies. Be honest about which signal you're following." },
    ],
  },
  {
    id: "personal_life",
    category: "Timing",
    cosmicPrompt: "Venus is asking about the rest of your life.",
    question: "Is the timing right for the rest of your life — not just your art?",
    subtext: "Residencies don't exist in a vacuum. Relationships, caregiving, health, leases, pets — these are real logistics that artists consistently cite as the hardest part of saying yes. Programs like WSW now offer childcare stipends ($350/week) because they know this matters.",
    answers: [
      { label: "Yes — my life can absorb this absence right now", value: 3, insight: "Your life is in alignment. The cosmos rarely see this. Don't waste it." },
      { label: "Mostly, but I'll need to arrange some things (pet care, subletting, etc.)", value: 2, insight: "Logistics are solvable. The universe respects a plan." },
      { label: "It would cause real strain on my relationships or responsibilities", value: 0, insight: "Art that costs you your relationships isn't art — it's avoidance. The stars said it, not us." },
      { label: "I have caregiving responsibilities that make this very complicated", value: 0, insight: "Caregiving is real. If the program doesn't accommodate it, the program isn't ready for you." },
    ],
  },
  {
    id: "program_quality",
    category: "Trust",
    cosmicPrompt: "The cosmos are running a background check.",
    question: "Have you researched this program — reviews, alumni experiences, red flags?",
    subtext: "Not every program that accepts you deserves your yes. Acceptance rates at top programs: Hedgebrook under 3%, Fine Arts Work Center under 2%, MacDowell 10–20%. Programs that accept everyone who pays aren't selective — they're transactional. Check RMAR reviews, alumni CVs, and Alliance of Artist Communities membership. As one executive director put it: \"A true residency will offer free lodging and even a stipend. If they need you to pay tuition, reconsider.\"",
    answers: [
      { label: "Yes — reviews are strong, alumni are real, the program is established", value: 3, insight: "Verified by the cosmos and by actual humans. A rare alignment." },
      { label: "Somewhat — I've read the website but haven't dug deeper", value: 1, insight: "Websites are brochures. The truth lives in alumni experiences. Dig deeper." },
      { label: "Not really — I applied broadly and this one said yes", value: 0, insight: "An acceptance isn't a recommendation. The universe wants you to know the difference." },
      { label: "The program is new — not much info exists yet", value: 1, insight: "New doesn't mean bad. But it means risk. The cosmos suggest asking for references." },
    ],
  },
  {
    id: "gut_check",
    category: "Instinct",
    cosmicPrompt: "The universe has one final question.",
    question: "When you imagine yourself there — in that studio, in that place, doing that work — what do you feel?",
    subtext: "After all the logistics, the math, and the research — your gut knows something your spreadsheet doesn't. But only if you've done the work above first. Intuition without information is just anxiety wearing a costume.",
    answers: [
      { label: "Genuinely excited — I can see myself making important work there", value: 3, insight: "The signal is clear. The cosmos, your gut, and your practice all agree." },
      { label: "Nervous but hopeful — which feels right for growth", value: 2, insight: "Productive discomfort. The universe's favorite flavor of yes." },
      { label: "Anxious — more dread than excitement", value: 0, insight: "Dread is data. The cosmos don't confuse anxiety with ambition." },
      { label: "Obligated — like I should want this more than I do", value: 0, insight: "\"Should\" is not a reason. The stars don't respond to guilt." },
    ],
  },
]

// ── Cosmic result messages ──

type ResultTier = {
  min: number
  max: number
  title: string
  emoji: string
  verdict: string
  explanation: string
  advice: string
  colorClass: string
}

const RESULT_TIERS: ResultTier[] = [
  {
    min: 0,
    max: 8,
    title: "The Stars Say: Not This One",
    emoji: "🌑",
    verdict: "The cosmos are holding up a stop sign.",
    explanation: "This isn't about your worth as an artist — it's about the math, the timing, or the fit. Multiple signals are telling you this opportunity would cost more than it gives. Going into debt, straining relationships, or accepting something you haven't researched isn't brave. It's expensive.",
    advice: "Decline with clarity, not guilt. The right opportunity will come when the conditions are right. In the meantime: save, research, and apply to programs that actually match your needs. The cosmos don't reward martyrdom.",
    colorClass: "from-slate-100 to-stone-100",
  },
  {
    min: 9,
    max: 15,
    title: "The Stars Say: Proceed with Caution",
    emoji: "🌗",
    verdict: "The cosmos see potential — but also loose ends.",
    explanation: "Some signals are strong, but others are flickering. Maybe the finances are tight. Maybe the timing is off. Maybe you haven't done enough research. This isn't a no — it's a \"not yet\" or a \"not without changes.\"",
    advice: "Before you accept: close the gaps. Can you negotiate a stipend increase, defer your start date, or find supplemental funding? State arts councils, fiscal sponsors like Fractured Atlas, and foundation grants exist for exactly this. Don't say yes to the opportunity as-is if the as-is doesn't work.",
    colorClass: "from-amber-50 to-yellow-50",
  },
  {
    min: 16,
    max: 22,
    title: "The Stars Say: This Is Your Moment",
    emoji: "🌕",
    verdict: "The cosmic signals are aligned.",
    explanation: "The finances work (or are close enough). The timing makes sense. The program is legitimate. The location serves your practice. Your life can absorb the absence. This is what a real opportunity looks like — not perfect, but right.",
    advice: "Accept it. Do your logistics. Tell the people who need to know. Pack the things that matter — and leave room for what you'll find there. The universe doesn't hand these out randomly. You earned this alignment.",
    colorClass: "from-emerald-50 to-teal-50",
  },
  {
    min: 23,
    max: 30,
    title: "The Stars Say: The Universe Built This for You",
    emoji: "✨",
    verdict: "Every signal is lit. This is cosmically unambiguous.",
    explanation: "Financial stability. Career alignment. Geographic resonance. Program quality. Personal readiness. Your gut says yes. The data says yes. The cosmos rarely agree this loudly.",
    advice: "Stop reading this and go accept it. Then come back and write the review when you're done — other artists need to know about this one.",
    colorClass: "from-violet-50 to-indigo-50",
  },
]

function getResultTier(score: number): ResultTier {
  return RESULT_TIERS.find((t) => score >= t.min && score <= t.max) || RESULT_TIERS[0]
}

// ── Cosmic intros for the start screen ──

const COSMIC_OPENERS = [
  "You got the acceptance email. Your heart did a thing. Now what?",
  "Congratulations — and also: let's slow down for a second.",
  "The cosmos sent you an opportunity. But the cosmos also sent you this quiz.",
  "An acceptance letter is not the same as a green light.",
  "Before you book the flight, the universe has some questions.",
  "Your inbox has news. The stars have opinions.",
]

export default function OpportunityQuiz() {
  const [phase, setPhase] = useState<"intro" | "quiz" | "results">("intro")
  const [currentQ, setCurrentQ] = useState(0)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [selectedInsights, setSelectedInsights] = useState<string[]>([])
  // Email-results capture (no account needed). The honeypot guards against
  // bots; the form submits the result payload + email to /api/quiz/email-results.
  const [emailValue, setEmailValue] = useState("")
  const [honeypot, setHoneypot] = useState("")
  const [emailStatus, setEmailStatus] = useState<"idle" | "loading" | "done" | "error">("idle")
  const [emailMessage, setEmailMessage] = useState("")
  const [opener] = useState(() => COSMIC_OPENERS[Math.floor(Math.random() * COSMIC_OPENERS.length)])

  const handleAnswer = useCallback((questionId: string, value: number, insight: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
    setSelectedInsights((prev) => [...prev, insight])

    if (currentQ < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQ((q) => q + 1), 300)
    } else {
      setTimeout(() => setPhase("results"), 500)
    }
  }, [currentQ])

  const totalScore = Object.values(answers).reduce((sum, v) => sum + v, 0)
  const result = getResultTier(totalScore)
  const progress = phase === "quiz" ? ((currentQ + 1) / QUESTIONS.length) * 100 : 0

  const restart = () => {
    setPhase("intro")
    setCurrentQ(0)
    setAnswers({})
    setSelectedInsights([])
  }

  // ── Intro screen ──
  if (phase === "intro") {
    return (
      <div className="animate-fade-in">
        {/* Cosmic gradient header */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-violet-100/80 via-indigo-50/60 to-transparent p-8 sm:p-12 mb-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(139,92,246,0.08),transparent_70%)]" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-violet-500" />
              <span className="text-xs font-semibold uppercase tracking-widest text-violet-600">
                Cosmic Gut Check
              </span>
            </div>
            <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold italic leading-tight mb-4 text-[var(--foreground)]">
              Should You Take<br />This Opportunity?
            </h2>
            <p className="text-base sm:text-lg text-[var(--muted-foreground)] max-w-xl leading-relaxed">
              {opener}
            </p>
          </div>
        </div>

        {/* Context block */}
        <div className="space-y-4 mb-8">
          <p className="text-sm leading-relaxed text-[var(--muted-foreground)]">
            You got accepted to a residency, fellowship, grant, or workshop. The excitement is real.
            But so are your rent payments, your relationships, and the $500 you forgot to budget for materials.
          </p>
          <p className="text-sm leading-relaxed text-[var(--muted-foreground)]">
            This quiz won&rsquo;t tell you what to do. But it will ask the questions the acceptance email didn&rsquo;t —
            the ones about money, timing, logistics, and whether this specific opportunity actually serves your
            practice right now.
          </p>
          <p className="text-sm leading-relaxed text-[var(--foreground)] font-medium">
            10 questions. Honest answers only. The cosmos don&rsquo;t do polite.
          </p>
        </div>

        {/* Data callout */}
        <div className="bg-[var(--secondary)] rounded-xl p-5 mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--primary)] mb-2">
            Why this quiz exists
          </p>
          <p className="text-sm text-[var(--muted-foreground)] leading-relaxed">
            Artists spend $25&ndash;$50 per application &mdash; one artist spent $600 on fees in a single year.
            Total residency costs are underestimated by 30&ndash;40%. Even &ldquo;fully funded&rdquo; programs may not
            cover travel ($200&ndash;$2,000), materials ($100&ndash;$1,000+), or lost income. W.A.G.E. warns that
            &ldquo;anyone who can&rsquo;t afford to work for free generally can&rsquo;t afford to enter the field.&rdquo;
            Research from Fractured Atlas, the Alliance of Artist Communities, Artwork Archive, W.A.G.E., and hundreds
            of artist experiences informed every question here.
          </p>
        </div>

        <button
          onClick={() => setPhase("quiz")}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--foreground)] text-[var(--background)] text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          Begin the reading
          <ArrowRight size={16} />
        </button>
      </div>
    )
  }

  // ── Quiz screen ──
  if (phase === "quiz") {
    const q = QUESTIONS[currentQ]
    return (
      <div className="animate-fade-in" key={q.id}>
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-violet-600">
              {q.category}
            </span>
            <span className="text-xs text-[var(--muted-foreground)] tabular-nums">
              {currentQ + 1} / {QUESTIONS.length}
            </span>
          </div>
          <div className="h-1 bg-[var(--muted)] rounded-full overflow-hidden">
            <div
              className="h-full bg-violet-400 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Cosmic prompt */}
        <p className="text-sm italic text-violet-600 mb-3 font-editorial">
          {q.cosmicPrompt}
        </p>

        {/* Question */}
        <h3 className="text-xl sm:text-2xl font-semibold leading-snug mb-3 text-[var(--foreground)]">
          {q.question}
        </h3>

        {/* Data context */}
        <p className="text-sm text-[var(--muted-foreground)] leading-relaxed mb-8">
          {q.subtext}
        </p>

        {/* Answer options */}
        <div className="space-y-3">
          {q.answers.map((a, i) => (
            <button
              key={i}
              onClick={() => handleAnswer(q.id, a.value, a.insight)}
              className="w-full text-left p-4 rounded-xl border border-[var(--border)] hover:border-violet-300 hover:bg-violet-50/50 transition-all duration-150 group"
            >
              <span className="text-sm font-medium text-[var(--foreground)] group-hover:text-violet-700 transition-colors">
                {a.label}
              </span>
            </button>
          ))}
        </div>

        {/* Back button */}
        {currentQ > 0 && (
          <button
            onClick={() => setCurrentQ((q) => q - 1)}
            className="inline-flex items-center gap-1.5 mt-6 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
          >
            <ArrowLeft size={14} />
            Previous question
          </button>
        )}
      </div>
    )
  }

  // ── Results screen ──
  return (
    <div className="animate-fade-in">
      {/* Result header */}
      <div className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${result.colorClass} p-8 sm:p-12 mb-8`}>
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-violet-500" />
            <span className="text-xs font-semibold uppercase tracking-widest text-violet-600">
              Your Reading
            </span>
          </div>
          <div className="text-4xl mb-4">{result.emoji}</div>
          <h2 className="font-editorial text-3xl sm:text-4xl font-semibold italic leading-tight mb-3 text-[var(--foreground)]">
            {result.title}
          </h2>
          <p className="text-base sm:text-lg font-medium text-[var(--foreground)]">
            {result.verdict}
          </p>
        </div>
      </div>

      {/* Email me my results — high-intent email capture */}
      <div className="mb-8 border border-violet-200 bg-violet-50/50 rounded-xl p-4 sm:p-5">
        {emailStatus === "done" ? (
          <div className="flex items-center gap-2 text-[13px] text-green-700">
            <span className="inline-flex w-5 h-5 rounded-full bg-green-100 items-center justify-center">✓</span>
            <span>{emailMessage}</span>
          </div>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              if (!emailValue.trim()) return
              setEmailStatus("loading")
              setEmailMessage("")
              try {
                const res = await fetch("/api/quiz/email-results", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    email: emailValue.trim(),
                    score: totalScore,
                    totalQuestions: QUESTIONS.length,
                    resultTitle: result.title,
                    resultVerdict: result.verdict,
                    resultExplanation: result.explanation,
                    resultAdvice: result.advice,
                    insights: selectedInsights.map((text, i) => ({
                      category: QUESTIONS[i]?.category ?? "",
                      text,
                    })),
                    website: honeypot,
                  }),
                })
                const data = await res.json().catch(() => ({}))
                if (res.ok) {
                  setEmailStatus("done")
                  setEmailMessage("Sent — check your inbox. Welcome to the newsletter.")
                } else {
                  setEmailStatus("error")
                  setEmailMessage(data.error ?? "Try again in a moment.")
                }
              } catch {
                setEmailStatus("error")
                setEmailMessage("Network error. Try again.")
              }
            }}
            className="flex flex-col gap-2"
          >
            <label className="text-sm font-semibold text-[var(--foreground)]">
              Email me my reading
            </label>
            <p className="text-xs text-[var(--muted-foreground)]">
              Get your results, the full explanation, and your insights in your inbox. You&apos;ll also get our weekly digest of new open calls.
            </p>
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
              aria-hidden="true"
            />
            <div className="flex gap-2 mt-1">
              <input
                type="email"
                required
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder="you@email.com"
                disabled={emailStatus === "loading"}
                className="flex-1 min-w-0 px-3 py-2 text-sm border border-violet-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={emailStatus === "loading"}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors disabled:opacity-60"
              >
                {emailStatus === "loading" ? "Sending…" : "Send"}
              </button>
            </div>
            {emailStatus === "error" && <p className="text-xs text-red-600 mt-1">{emailMessage}</p>}
          </form>
        )}
      </div>

      {/* Score context */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm font-semibold text-[var(--foreground)]">
            Alignment Score
          </span>
          <span className="text-sm tabular-nums text-[var(--muted-foreground)]">
            {totalScore} / {QUESTIONS.length * 3}
          </span>
        </div>
        <div className="h-2 bg-[var(--muted)] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${(totalScore / (QUESTIONS.length * 3)) * 100}%`,
              background: totalScore >= 23 ? "#8b5cf6" : totalScore >= 16 ? "#10b981" : totalScore >= 9 ? "#f59e0b" : "#64748b",
            }}
          />
        </div>
      </div>

      {/* Explanation */}
      <div className="space-y-4 mb-8">
        <p className="text-sm leading-relaxed text-[var(--foreground)]">
          {result.explanation}
        </p>
        <p className="text-sm leading-relaxed text-[var(--muted-foreground)]">
          {result.advice}
        </p>
      </div>

      {/* Insights from answers */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-[var(--primary)] mb-4">
          What the cosmos noted
        </h3>
        <div className="space-y-3">
          {selectedInsights.map((insight, i) => (
            <div
              key={i}
              className="flex gap-3 p-3 rounded-lg bg-[var(--secondary)]"
            >
              <span className="text-xs font-semibold text-violet-500 uppercase tracking-wider shrink-0 mt-0.5 w-16">
                {QUESTIONS[i]?.category}
              </span>
              <p className="text-sm text-[var(--muted-foreground)] italic font-editorial leading-relaxed">
                {insight}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Resources callout */}
      <div className="bg-[var(--secondary)] rounded-xl p-6 mb-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--primary)] mb-3">
          Do the homework
        </p>
        <ul className="space-y-2 text-sm text-[var(--muted-foreground)]">
          <li>
            <a href="/resources/funding-guide" className="text-[var(--primary)] hover:underline">How to Fund Your Residency</a>
            {" "}&mdash; grants, fellowships, and creative budgeting strategies
          </li>
          <li>
            <a href="/resources/fee-vs-free" className="text-[var(--primary)] hover:underline">Fee-Based vs Free Residencies</a>
            {" "}&mdash; understanding what you&rsquo;re paying for
          </li>
          <li>
            <a href="/resources/first-residency" className="text-[var(--primary)] hover:underline">What to Expect at Your First Residency</a>
            {" "}&mdash; the practical reality, not the Instagram version
          </li>
          <li>
            <a href="/resources/international-guide" className="text-[var(--primary)] hover:underline">International Residencies Guide</a>
            {" "}&mdash; visas, insurance, and logistics for going abroad
          </li>
          <li>
            <a href="/directory" className="text-[var(--primary)] hover:underline">Browse the Directory</a>
            {" "}&mdash; read reviews from artists who&rsquo;ve actually been there
          </li>
        </ul>
      </div>

      {/* Data sources */}
      <div className="mb-8 p-4 rounded-lg border border-[var(--border)]">
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--muted-foreground)] mb-2">
          Sources informing this quiz
        </p>
        <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">
          Fractured Atlas residency cost analysis &middot; Alliance of Artist Communities fees, stipends &amp; funding data &middot;
          Artwork Archive true cost research &middot; W.A.G.E. on residency programs &middot; U.S. Bureau of Labor Statistics
          artist wage data &middot; DACS/University of Glasgow artist income survey &middot; LA Artist Census (2019) &middot;
          Artist community discussions on r/ArtistLounge and r/ContemporaryArt &middot;
          Program data from MacDowell, Yaddo, Vermont Studio Center, Bemis Center, Skowhegan, Hedgebrook,
          Fine Arts Work Center, Gasworks, and WSW &middot; Bad at Sports residency survey &middot;
          Individual artist financial disclosures and residency cost breakdowns
        </p>
      </div>

      {/* Restart */}
      <button
        onClick={restart}
        className="inline-flex items-center gap-2 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
      >
        <RotateCcw size={14} />
        Take the quiz again
      </button>

      {/* Disclaimer */}
      <p className="mt-8 text-xs text-[var(--muted-foreground)] italic">
        This quiz is a thinking tool, not financial or career advice. Your situation is yours.
        The cosmos suggest talking to a real human (mentor, financial advisor, or trusted friend) before making big decisions.
      </p>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  )
}
